/* =============================================================================
   VIEWS / ADMIN / ADMIN-ORDER-ENTRY-SUBMIT — Submit-Pipeline
   =============================================================================
   v0.76.0-beta / Session 70 / REF-5 Cut 4/4
   ----------------------------------------------------------------------------
   Submit-Pipeline: Vorflug-Checks (Delivery-Adresse + Zone, Mindestbestellwert,
   Pickup-Slot) → createAdmin-RPC → Reservation/Occupancy-Linking → UI-Feedback.

   Cross-Module:
     - _state, getState, lineToRpcItem aus admin-order-entry-state.js
     - _openQuickAddDeliveryZoneModal aus admin-order-entry-delivery.js
     - Modal-Close via window.closeAdminOrderEntry()
============================================================================ */

import { showToast, dateToISO } from '../../utils/helpers.js';
import { _state, getState, lineToRpcItem } from './admin-order-entry-state.js';
import { _openQuickAddDeliveryZoneModal } from './admin-order-entry-delivery.js';
import { invalidateItems } from '../../core/order-items-cache.js';
// S239 Cut A0 — Bestellung aus einer Reservierung = „Gäste sind da" (Auto-Check-in).
import { ensureReservationCheckedIn } from './reservation-checkin.js';

/**
 * S239-HF1 — `orders.occupancy_id` nachträglich setzen, MIT sichtbarem Fehler.
 *
 * BEFUND aus dem Cut-A0-Test: der Link scheiterte 40 Minuten lang still am
 * CHECK `orders_polymorphic_link` (phase_55) — die Order bekam nie ihre
 * `occupancy_id`, der Tisch-Warenkorb blieb leer und `auto_end_occupancy_for_order`
 * beendete die Belegung beim Kassieren nicht. Der Fehler war doppelt unsichtbar:
 *
 *   1. `supabase.from(...).update(...)` **wirft nicht** bei einem Constraint-
 *      Verstoß — PostgREST liefert `{ error }` zurück. Der umschließende
 *      try/catch konnte also gar nie greifen.
 *   2. Selbst der geloggte Fall war nur ein `console.warn`.
 *
 * Deshalb: `error` explizit auswerten UND den Operator informieren. Ein
 * fehlgeschlagener Link ist operativ relevant (kein „Rechnung offen" auf der
 * Kachel, Belegung bleibt nach dem Kassieren stehen) — er darf nicht still sein.
 * Die DB-Seite fixt phase_172 (XOR-CHECK → Konsistenz-Trigger).
 *
 * @returns {Promise<boolean>} true, wenn der Link steht
 */
async function _linkOrderToOccupancy(orderId, occupancyId, context) {
  try {
    const { error } = await window.App.supabase
      .from('orders').update({ occupancy_id: occupancyId }).eq('id', orderId);
    if (error) throw error;
    return true;
  } catch (linkErr) {
    console.error(`[submitAdminOrder] occupancy_id-Link (${context}) fehlgeschlagen:`, linkErr);
    showToast('Bestellung konnte nicht mit dem Tisch verknüpft werden — bitte im Belegungsplan prüfen.', 'error');
    return false;
  }
}

// — Submit: createAdmin + Cleanup ————————————————————————————

export async function submitAdminOrder() {
  if (_state.serviceType === 'dine-in' && !_state.tableId) {
    return showToast('Bitte zuerst einen Tisch wählen.', 'error');
  }
  // S60 — Delivery-Vorflug-Checks
  if (_state.serviceType === 'delivery') {
    const a = _state.deliveryAddress;
    if (!a.name || !a.phone || !a.street || !a.plz || !a.city) {
      return showToast('Bitte alle Pflichtfelder der Lieferadresse füllen.', 'error');
    }
    // S88 B/C — E-Mail bei Lieferung Pflicht (Bestätigung + Self-Cancel-Link).
    if (!(_state.customerEmail || '').trim()) {
      return showToast('Bitte E-Mail für die Lieferung angeben (Bestätigung + Storno-Link).', 'error');
    }
    // S61 — Statt Hard-Block-Toast bei !deliveryZone bieten wir
    // owner/manager-Berechtigten direkt das Quick-Add-Modal an.
    // Operativer Show-Stopper wird damit umgangen, ohne dass jemand
    // erst in Settings → Lieferung wechseln muss.
    if (!_state.deliveryZone) {
      const plz = (a.plz || '').trim();
      const canAddZone = window.App && window.App.auth
        && typeof window.App.auth.canEdit === 'function'
        && window.App.auth.canEdit('settings.rules');
      if (canAddZone && plz && /^\d{4,5}$/.test(plz)) {
        _openQuickAddDeliveryZoneModal(plz);
        return;
      }
      return showToast(
        '⚠️ PLZ nicht im Liefergebiet. Bitte Manager/Owner kontaktieren ' +
        'oder in Settings → Lieferung → Zonen ergänzen.',
        'error'
      );
    }
  }
  // Menu-Picker exportiert die Funktionen als getOrder + validateOrder
  // (siehe menu-picker.js, Backward-Compat-Block am Modul-Ende).
  if (typeof window.getOrder !== 'function') {
    return showToast('Menu-Picker nicht geladen.', 'error');
  }
  if (typeof window.validateOrder === 'function') {
    const vr = window.validateOrder();
    if (!vr.ok) {
      return showToast(vr.errors[0] || 'Bitte vervollständige die Bestellung.', 'error');
    }
  }
  const order = window.getOrder();
  // S169 P4 — Deal-Positionen zerfallen in echte order_items (je Komponente eine
  // Zeile, Deal-Preis + eigener tax_rate, verklammert via customizations.deal.line_uid).
  const dealItems = (typeof window.getDealOrderItems === 'function') ? window.getDealOrderItems() : [];
  if (!order || !Array.isArray(order.items) || (order.items.length === 0 && dealItems.length === 0)) {
    return showToast('Bitte mindestens ein Gericht auswählen.', 'error');
  }

  const state = getState();
  const menuList = Array.isArray(state.menu) ? state.menu : [];
  const items = order.items.map(line => lineToRpcItem(line, menuList, state.settings));
  if (dealItems.length) items.push(...dealItems);   // Deal-Komponenten in dieselbe Pipeline

  // Pickup-Slot:
  // - Dine-in (Tresen/Tisch): pickup_time = now(). Der Gast sitzt schon,
  //   die Küche soll sofort lostakten. (UX-Win S24)
  // - Takeaway aus dem Cockpit: pickup_time = now() + orderPrepTimeMin
  //   (Gast kommt in prepMin Minuten).
  const prepMin = Number((state.settings && state.settings.orderPrepTimeMin) || 15);
  const now = new Date();
  const isDineIn = _state.serviceType === 'dine-in';

  // S61 — Reservation-Kontext: pickup-Zeit aus Reservation übernehmen,
  // damit die Order als Pre-Order behandelt wird (KDS-Badge lila bis
  // 60 Min vor pickup, dann automatisch scharf). Walk-In ohne Reservation
  // = pickup jetzt; Takeaway = jetzt + prepMin.
  let pickupDate, pickupTime;
  const resCtx = _state.reservationContext;
  if (resCtx && resCtx.date && resCtx.time) {
    pickupDate = resCtx.date;
    pickupTime = String(resCtx.time).slice(0, 5); // "HH:MM" — falls Sekunden mit dran sind
  } else {
    const pickupAt = isDineIn ? now : new Date(now.getTime() + prepMin * 60 * 1000);
    // S60 FIX.2 — pickupDate MUSS in Restaurant-TZ berechnet werden, nicht UTC.
    // Sonst gibt `.toISOString()` bei lokaler Zeit nach Mitternacht das Datum
    // des Vortages → Orders verschwinden aus KDS/Theke + Buchungstag verkehrt.
    pickupDate = dateToISO(pickupAt);
    pickupTime = String(pickupAt.getHours()).padStart(2, '0') + ':' +
                 String(pickupAt.getMinutes()).padStart(2, '0');
  }

  // S61 — Doppel-Hard-Block entfällt: der Vorflug-Check oben (Z. ~926)
  // führt jetzt direkt zum Quick-Add-Modal oder Manager-Hinweis-Toast.

  // S60 — Mindestbestellwert-Check bei Lieferung
  if (_state.serviceType === 'delivery' && _state.deliveryZone) {
    const itemsCents = items.reduce((s, it) => s + (it.unitPriceCents * it.qty), 0);
    const min = _state.deliveryZone.min_order_cents || 0;
    if (itemsCents < min) {
      const diff = ((min - itemsCents) / 100).toFixed(2).replace('.', ',');
      return showToast(`Mindestbestellwert ${(min/100).toFixed(2).replace('.', ',')} € — noch ${diff} € fehlen.`, 'error');
    }
  }

  // S60 W1 — Bei Hier-Essen die dineInForm-Felder mit übergeben, damit Customer-CRM
  // greift (auch wenn Gast nicht in Form eingetragen ist, läuft die Order korrekt).
  const dinF = _state.dineInForm;
  const isDelivery = _state.serviceType === 'delivery';

  const req = {
    serviceType: _state.serviceType,
    tableId:     _state.tableId || null,
    pickupDate,
    pickupTime,
    items,
    notes:       _state.notes || (order.specialRequests || null),
    // Delivery-spezifische Felder (S59-Schema: orders.delivery_address + customer-Felder)
    deliveryAddress: isDelivery ? {
      ..._state.deliveryAddress,
      zone_id:         _state.deliveryZone && _state.deliveryZone.zone_id,
      // S61 — zone_name mitschicken, damit KDS/Theke das Liefergebiet im
      // Badge anzeigen können (statt der weniger aussagekräftigen Stadt).
      zone_name:       _state.deliveryZone && _state.deliveryZone.name,
      fee_cents:       _state.deliveryZone && _state.deliveryZone.fee_cents,
      min_order_cents: _state.deliveryZone && _state.deliveryZone.min_order_cents
    } : null,
    customerName:  isDelivery ? _state.deliveryAddress.name  : (isDineIn ? (dinF.guestName  || null) : null),
    customerPhone: isDelivery ? _state.deliveryAddress.phone : (isDineIn ? (dinF.guestPhone || null) : null),
    // REF-7b (S75): vereinheitlichte Mail-E-Mail (gilt für alle 3 Service-Typen,
    // erfasst im Mail-Opt-In-Block) + die 4 Opt-In-Booleans → create_admin_order.
    customerEmail: (_state.customerEmail || '').trim() || null,
    mailOptIn:     _state.mailOptIn,
    // S90 phase_86 — Reservierungs-Kontext: reservation_id schon BEI create setzen,
    // damit der Order-Trigger reservation_with_order_confirmed (statt order_confirmed)
    // emittiert + die E-Mail aus der Reservierung zieht (Timing-fest, kein Post-UPDATE).
    reservationId: (_state.reservationContext && _state.reservationContext.id) || null
  };

  const isReservationOrder = !!(_state.reservationContext && _state.reservationContext.id);

  // S135 P4 — Kern-Invariante „eine offene Order pro Tisch/Reservierung":
  // Hat die Belegung (occupancy_id) bzw. Reservierung (reservation_id) schon eine
  // offene UNBEZAHLTE Order? → Items dort ANHÄNGEN (neue Runde) statt eine zweite
  // Order zu erzeugen (= der „geteilte Rechnungen"-Bug). Sonst regulärer
  // Create-Pfad unten. Greift für ALLE occupancy-gebundenen Einstiege:
  // Belegungs-Modal (openOrderForOccupancy → _pendingOccupancyIdForOrder),
  // Walk-In-Folgebestellung, Reservierungs-Folgebestellung.
  // S239 Cut A0 — „Bestellung aufnehmen" aus einer Reservierung heißt: die Gäste
  // sitzen da → Auto-Check-in VOR der Order. Ab hier verhält sich die
  // Reservierung wie ein Walk-In (Belegung trägt Überfällig-Uhr, Auto-Release,
  // Kassieren, Freigabe), und die Order hängt an `occupancy_id`.
  // `ensureReservationCheckedIn` ist bewusst tolerant: für eine Reservierung der
  // ZUKUNFT (oder ohne Tisch) liefert sie null und die Bestellung läuft wie
  // bisher ohne Belegung — der S119-Phantom-Walk-In bleibt damit ausgeschlossen.
  let resOccupancyId = null;
  if (isDineIn && isReservationOrder) {
    resOccupancyId = await ensureReservationCheckedIn(_state.reservationContext.id);
  }
  const occAppendKey = isReservationOrder
    ? resOccupancyId
    : (isDineIn ? (window._pendingOccupancyIdForOrder || null) : null);
  const resAppendKey = isReservationOrder ? _state.reservationContext.id : null;
  const openOrder = _findOpenOrderForContainer(occAppendKey, resAppendKey);
  if (openOrder) {
    try {
      const updated = await window.App.storage.orders.addItems(openOrder.id, items);
      window._pendingOccupancyIdForOrder = null;
      invalidateItems(openOrder.id);   // Items-Cache leeren → KDS/Theke/Topbar laden die neue Runde
      if (window.App.events) {
        try { await window.App.events.emit('order:items_appended', updated || { id: openOrder.id }); } catch (_) {}
      }
      showToast(`An offene Order ${openOrder.orderNumber || ''} angehängt — neue Runde läuft in die Küche.`, 'success');
      if (typeof window.closeAdminOrderEntry === 'function') window.closeAdminOrderEntry();
      if (typeof window.refreshOrdersTabCounter === 'function') window.refreshOrdersTabCounter();
      return;
    } catch (err) {
      console.error('[submitAdminOrder][append]', err);
      // OPERATOR_REQUIRED öffnet global das PIN-Overlay (_throwWriteError).
      if (!(err && err.operatorRequired)) {
        showToast('Anhängen an offene Order fehlgeschlagen: ' + (err.message || err), 'error');
      }
      return;
    }
  }

  let created;
  try {
    created = await window.App.storage.orders.createAdmin(req);
  } catch (err) {
    console.error('[submitAdminOrder]', err);
    return showToast('Bestellung fehlgeschlagen: ' + (err.message || err), 'error');
  }

  // S61/S90 — Reservierungs-Kontext: die Verknüpfung orders.reservation_id setzt
  // jetzt create_admin_order selbst (phase_86, BEI create — timing-fest für den
  // Mail-Trigger). Der frühere Post-UPDATE entfällt. Audit-Spur bleibt.
  if (_state.reservationContext && _state.reservationContext.id && created && created.id) {
    if (typeof window.audit === 'function') {
      window.audit('reservation.got_order', {
        reservation_id: _state.reservationContext.id,
        order_id: created.id,
        order_number: created.orderNumber || created.order_number || null
      });
    }
  }

  // S60 W1+W2 — Bei Hier-Essen: occupancy_id-Linking.
  //   Fall 1 (Order kommt aus aktivem Walk-In, „Bestellung aufnehmen" im Belegungs-Modal):
  //     window._pendingOccupancyIdForOrder ist gesetzt → wir verknüpfen die Order mit der
  //     bestehenden table_occupancy. Keine neue Occupancy anlegen.
  //   Fall 2 (Neue Hier-Essen-Order aus Bestellung-aufnehmen → Jetzt bestellen):
  //     Neue table_occupancy anlegen + orders.occupancy_id auf neue Occupancy linken.
  // S119 — Reservierungs-Order legt KEINE Walk-In-Belegung an: die Reservierung
  // repräsentiert den Tisch bereits (eigene Belegung bei Bestätigung), und dieser
  // Pfad bedient eine Reservierung für die ZUKUNFT → ein Walk-In „jetzt" ist sinnlos
  // und tauchte als Phantom-Walk-In am reservierten Tisch auf. Beide Zweige (Link +
  // Auto-Create) werden für reservierungs-verknüpfte Orders übersprungen.
  // (isReservationOrder ist oben — bei der P4-Append-Weiche — schon definiert.)
  // S239 Cut A0 — Reservierungs-Order MIT Check-in-Belegung: die Order wird an
  // genau diese Belegung gehängt (Fall 1 unten). Ohne Check-in (Zukunfts-
  // Reservierung) bleibt der S119-Guard: KEINE Belegung anlegen.
  if (isDineIn && isReservationOrder && resOccupancyId && created && created.id && window.App.supabase) {
    const linked = await _linkOrderToOccupancy(created.id, resOccupancyId, 'Check-in');
    if (linked && typeof window.audit === 'function') {
      window.audit('reservation.order_linked_to_checkin', {
        reservation_id: _state.reservationContext.id,
        occupancy_id: resOccupancyId,
        order_id: created.id
      });
    }
  }

  if (isDineIn && !isReservationOrder && _state.tableId && created && created.id && window.App.supabase) {
    const existingOccupancyId = window._pendingOccupancyIdForOrder || null;
    if (existingOccupancyId) {
      // Fall 1: existing Walk-In bekommt diese Order
      // S239-HF1: derselbe Helfer wie im Check-in-Zweig — auch hier war ein
      // fehlgeschlagener Link bisher unsichtbar (PostgREST wirft nicht).
      const linked = await _linkOrderToOccupancy(created.id, existingOccupancyId, 'existing');
      // S60 W4.9 — Audit-Entry: nachvollziehbar, wann dieser Walk-In bestellt hat
      if (linked && typeof window.audit === 'function') {
        window.audit('walk_in.got_order', {
          occupancy_id: existingOccupancyId,
          order_id: created.id,
          order_number: created.orderNumber || created.order_number || null,
          customer_id: created.customer_id || created.customerId || null
        });
      }
      window._pendingOccupancyIdForOrder = null;
    } else if (window.App.storage.tableOccupancies
               && typeof window.App.storage.tableOccupancies.create === 'function') {
      // Fall 2: neue Occupancy + Cross-Link
      // S61 FIX.7 — vor Auto-Create prüfen, ob schon eine Occupancy auf dem
      // Tisch existiert. Wenn ja: Order an die existierende linken statt
      // zweite Occupancy anzulegen (genau der S60-Doppelt-Bug-Case).
      try {
        const conflict = await window.App.storage.tableOccupancies.checkConflict(_state.tableId);
        if (conflict && conflict.has_conflict && conflict.occupancy && conflict.occupancy.id) {
          // S239-HF1: Fehler sichtbar machen (siehe _linkOrderToOccupancy).
          await _linkOrderToOccupancy(created.id, conflict.occupancy.id, 'conflict-link');
          if (typeof window.audit === 'function') {
            window.audit('walk_in.got_order_via_conflict_link', {
              occupancy_id: conflict.occupancy.id,
              order_id: created.id,
              order_number: created.orderNumber || created.order_number || null,
              note: 'Auto-Link statt zweiter Walk-In, Conflict-Check positiv'
            });
          }
          // S134c (Befund 4) — Auto-Link nicht mehr stumm: Operator sieht, dass
          // die Order an eine bestehende Belegung ging (statt frischer Belegung).
          try {
            const stTbl = (getState().tables || []).find(t => t.id === _state.tableId);
            showToast(`Order an bestehende Belegung von Tisch ${(stTbl && stTbl.name) || _state.tableId} angehängt.`, 'info');
          } catch (_) { /* nicht-fatal */ }
          // Fertig — keine neue Occupancy anlegen.
        } else {
          // Kein Konflikt: regulärer Auto-Create-Pfad.
          await _createOccupancyForOrder(created, dinF, _state);
        }
      } catch (autoErr) {
        console.warn('[submitAdminOrder] auto-occupancy fehlgeschlagen, fahre fort:', autoErr);
      }
      // (Auto-Create-Code ist in _createOccupancyForOrder ausgelagert.)
    }
  }

  // Event-Bus + Audit folgt durchs DB-Trigger schon; hier nur UI-Feedback.
  if (window.App.events) {
    try { await window.App.events.emit('order:created', created); } catch (_) {}
  }

  showToast(`Bestellung ${created.orderNumber || ''} angelegt — läuft in die Küche.`, 'success');
  if (typeof window.closeAdminOrderEntry === 'function') window.closeAdminOrderEntry();

  // Direkt zum KDS oder zur Bestellungen-Sicht — abhängig wo der Admin gerade ist.
  if (typeof window.refreshOrdersTabCounter === 'function') {
    window.refreshOrdersTabCounter();
  }
}

/**
 * S135 P4 — Findet die EINE offene (unbezahlte) Order eines Containers
 * (Belegung via occupancy_id ODER Reservierung via reservation_id) im Cache.
 * Genau diese Order nimmt Nachbestellungen auf (Append). Storno- und bezahlte
 * sowie cancelled Orders zählen nicht. Exportiert für Tests.
 */
export function _findOpenOrderForContainer(occId, resId) {
  if (!occId && !resId) return null;
  const orders = (getState().orders || []);
  return orders.find(o =>
    o && o.paymentStatus !== 'paid' && !o.isCorrection && o.status !== 'cancelled' &&
    ((occId && o.occupancyId === occId) || (resId && o.reservationId === resId))
  ) || null;
}

/**
 * S61 FIX.7 — Helper für Fall-2-Auto-Create einer table_occupancy nach
 * Order-Submit. Wird aufgerufen, wenn vorher kein Walk-In existierte und
 * der Conflict-Check sauber war.
 */
async function _createOccupancyForOrder(createdOrder, dinF, st) {
  try {
    // S134 P2 — Mehr-Tisch-Walk-In aus dem Assignment-Picker; Fallback [tableId].
    const assignedTableIds = (st.assignedTableIds && st.assignedTableIds.length)
      ? st.assignedTableIds.slice() : [st.tableId];
    const occ = await window.App.storage.tableOccupancies.create({
      tableId:          st.tableId,
      assignedTableIds: assignedTableIds,
      guests:           dinF.guests || 1,
      guestName:        dinF.guestName || null,
      guestPhone:       dinF.guestPhone || null,
      durationMin:      dinF.durationMin || 90,
      notes:            dinF.notes || null,
      customerId:       dinF.customerId || null
    });
    if (occ && occ.id) {
      try {
        await window.App.supabase.from('orders').update({ occupancy_id: occ.id }).eq('id', createdOrder.id);
        // S60 W4.9 — Audit-Entry für neue Occupancy + verknüpfte Order
        if (typeof window.audit === 'function') {
          window.audit('walk_in.got_order', {
            occupancy_id: occ.id,
            order_id: createdOrder.id,
            order_number: createdOrder.orderNumber || createdOrder.order_number || null,
            customer_id: dinF.customerId || null,
            created_with_order: true
          });
        }
      } catch (linkErr) {
        console.warn('[_createOccupancyForOrder] occupancy_id-Link (new) fehlgeschlagen:', linkErr);
      }
    }
  } catch (occErr) {
    console.warn('[_createOccupancyForOrder] table_occupancy-Anlage fehlgeschlagen:', occErr);
  }
}
